/**
 * TypeScript sequencer for UI display purposes
 * Replicates the logic from as/assembly/sequencer.ts for calculating sequence positions
 */

import { SAMPLE_RATE } from './bytecode.ts'
import type { NoteData, SequenceData } from './bytecode.ts'

export interface SequencerPosition {
  noteIndex: number
  positionInNote: number // 0-1, how far through the current note
  isNoteStart: boolean // true if at the very start of a note
  currentNote: NoteData
}

export type SequencerMode = 'walk' | 'slide' | 'adv' | 'advslide' | 'shuffle' | 'rand'

export class UISequencer {
  private data: SequenceData
  private noteStarts: number[] = []
  private totalSamples: number = 0
  private patternStartSample: number = -1
  private shuffledIndices: number[] | null = null
  private shuffleTriggerCount: number = 0
  private lastShuffleSeed: number = 0
  private oversamplingFactor: number = 1
  private sampleRate: number = SAMPLE_RATE
  private previousBpm: number = -1
  // State synced from WASM SequencerState
  private wasmState: {
    patternStartSample: number
    currentIndex: number
    shuffleTriggerCount: number
    shuffledIndices: Int32Array | null
    rotatedIndices: Int32Array | null
  } | null = null

  constructor(data: SequenceData, oversamplingFactor: number = 1, sampleRate: number = SAMPLE_RATE) {
    this.data = data
    this.oversamplingFactor = oversamplingFactor
    this.sampleRate = sampleRate
    this.updateTiming()
  }

  /**
   * Update sequence data while preserving runtime state
   */
  updateData(newData: SequenceData): void {
    const oldLength = this.data.notes.length
    const newLength = newData.notes.length

    this.data = newData

    console.log(`[UI-SEQUENCER] updateData: oldLength=${oldLength}, newLength=${newLength}`)
    for (let i = 0; i < Math.min(newData.notes.length, 10); i++) {
      const n = newData.notes[i]
      console.log(`  note[${i}]: duration=${n.duration}, velocity=${n.velocity}`)
    }

    // Reset pattern start if sequence length changed significantly
    if (newLength === 0 || (oldLength > 0 && Math.abs(newLength - oldLength) / oldLength > 0.5)) {
      this.patternStartSample = -1
      this.wasmState = null // Clear WASM state when sequence changes significantly
    }

    // Resize noteStarts if needed
    if (this.noteStarts.length < newData.notes.length) {
      this.noteStarts = new Array(newData.notes.length).fill(0)
    }
    else {
      // Zero out the portion we'll use
      for (let i = 0; i < newData.notes.length; i++) {
        this.noteStarts[i] = 0
      }
    }

    this.updateTiming()
  }

  /**
   * Recalculate note start positions and total sequence length
   */
  private updateTiming(bpm: number = 120): void {
    const samplesPerBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0

    this.totalSamples = 0
    let cumulativeFloat = 0

    // Ensure noteStarts array is large enough
    if (this.noteStarts.length < this.data.notes.length) {
      this.noteStarts = new Array(this.data.notes.length)
    }

    for (let n = 0; n < this.data.notes.length; n++) {
      this.noteStarts[n] = Math.floor(cumulativeFloat)
      const noteDuration = this.data.notes[n].duration * samplesPerBeat
      cumulativeFloat += noteDuration
    }
    this.totalSamples = Math.floor(cumulativeFloat)
  }

  /**
   * Calculate cycle length in samples for a given cycle length in notes
   */
  private calculateCycleLengthSamples(cycleLength: number, bpm: number = 120): number {
    const samplesPerBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0
    let cumulativeFloat = 0
    for (let n = 0; n < cycleLength && n < this.data.notes.length; n++) {
      const noteDuration = this.data.notes[n].duration * samplesPerBeat
      cumulativeFloat += noteDuration
    }
    return Math.floor(cumulativeFloat)
  }

  /**
   * Get current position in sequence for beat-based modes (walk, slide, shuffle)
   */
  getPosition(
    globalSampleCounter: number,
    bpm: number = 120,
    mode: SequencerMode = 'walk',
    seed: number = 0,
  ): SequencerPosition | null {
    if (this.data.notes.length === 0) {
      return null
    }

    const samplesPerBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0

    // Recalculate timing with current BPM
    this.updateTiming(bpm)

    // Align pattern start to bar boundary using the same logic as the AssemblyScript sequencer
    // This keeps the UI sequencer locked to the audio sequencer's beat grid
    // However, if we have synced WASM state, always respect it (WASM is the source of truth)
    // Only recalculate if:
    // 1. We've never synced from WASM (no WASM state), AND
    // 2. patternStartSample is invalid (< 0) OR counter went backwards significantly (reset)
    const samplesPerBar = Math.floor(samplesPerBeat)
    const hasSyncedWasmState = this.wasmState !== null
    const counterReset = this.patternStartSample >= 0 && globalSampleCounter < this.patternStartSample - 10000

    if (!hasSyncedWasmState && (this.patternStartSample < 0 || counterReset)) {
      const safeSamplesPerBar = samplesPerBar > 0 ? samplesPerBar : 1
      this.patternStartSample = globalSampleCounter - (globalSampleCounter % safeSamplesPerBar)
    }

    this.previousBpm = bpm

    // Calculate offset once
    const offsetSamples = this.data.offset !== 0 ? Math.floor(this.data.offset! * this.sampleRate) : 0

    // Calculate time relative to pattern start
    const timeSinceStart = globalSampleCounter - this.patternStartSample

    // Apply offset for pattern timing
    const patternSample = timeSinceStart + offsetSamples

    // Calculate position within sequence loop
    // Use cycleLength if set (for nested alternating patterns), otherwise use totalSamples
    const loopLength = this.data.cycleLength !== undefined
      ? this.calculateCycleLengthSamples(this.data.cycleLength, bpm)
      : this.totalSamples
    let seqPosition = 0
    if (loopLength > 0) {
      seqPosition = patternSample % loopLength
      // Handle negative modulo results (important for negative offsets)
      if (seqPosition < 0) {
        seqPosition += loopLength
      }

      // If we're in the first pattern cycle and patternStartSample is 0,
      // ensure we don't show notes from a previous cycle by clamping seqPosition
      // This prevents showing notes mid-sequence when playback starts
      if (this.patternStartSample === 0 && globalSampleCounter < loopLength) {
        // Clamp seqPosition to be within the first pattern cycle
        seqPosition = Math.min(seqPosition, loopLength - 1)
      }
    }

    // Find which note we're in
    // If using cycleLength, we need to map the cycle position to the correct note in the expanded sequence
    let noteIndex = 0
    if (this.noteStarts.length > 0 && loopLength > 0) {
      if (this.data.cycleLength !== undefined && this.data.cycleLength > 0) {
        // For nested alternating patterns, calculate which cycle we're in and map to note index
        const cycleIndex = Math.floor(patternSample / loopLength)
        const positionInCycle = seqPosition
        // Find which note in the cycle corresponds to this position
        // We need to compare against note starts within the cycle, not the full sequence
        let cycleNoteIndex = 0
        const numCycles = Math.floor(this.data.notes.length / this.data.cycleLength)
        let actualCycleIndex = cycleIndex % numCycles
        // Handle negative modulo results
        if (actualCycleIndex < 0) {
          actualCycleIndex += numCycles
        }
        const cycleStartIndex = actualCycleIndex * this.data.cycleLength

        // Calculate note starts relative to the cycle start
        const samplesPerBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0
        let cycleRelativeStart = 0
        for (let n = 0; n < this.data.cycleLength && (cycleStartIndex + n) < this.data.notes.length; n++) {
          const absoluteNoteIndex = cycleStartIndex + n
          const noteStartInCycle = cycleRelativeStart
          if (positionInCycle >= noteStartInCycle) {
            cycleNoteIndex = n
          }
          else {
            break
          }
          // Calculate next note start in cycle
          if (n < this.data.cycleLength - 1) {
            const noteDuration = this.data.notes[absoluteNoteIndex].duration * samplesPerBeat
            cycleRelativeStart += Math.floor(noteDuration)
          }
        }
        // Map to note index in expanded sequence
        const expandedNoteIndex = cycleStartIndex + cycleNoteIndex
        noteIndex = Math.min(expandedNoteIndex, this.data.notes.length - 1)
      }
      else {
        // Normal case: find note based on seqPosition
        const maxIndex = Math.min(this.noteStarts.length, this.data.notes.length)
        for (let n = 0; n < maxIndex; n++) {
          const noteStart = this.noteStarts[n]
          if (seqPosition >= noteStart) {
            noteIndex = n
          }
          else {
            break
          }
        }
      }
      // Clamp noteIndex to valid range
      if (noteIndex >= this.data.notes.length) {
        noteIndex = this.data.notes.length > 0 ? this.data.notes.length - 1 : 0
      }
    }

    // Handle shuffle mode
    if (mode === 'shuffle') {
      // Always prefer WASM synced shuffle state if available (it's the source of truth)
      if (this.wasmState && this.wasmState.shuffledIndices && this.wasmState.shuffledIndices.length > 0) {
        this.shuffledIndices = Array.from(this.wasmState.shuffledIndices)
        this.shuffleTriggerCount = this.wasmState.shuffleTriggerCount
      }
      else {
        // Fallback: calculate locally if WASM state not available
        // Update shuffle state if seed changed
        if (seed !== this.lastShuffleSeed) {
          this.lastShuffleSeed = seed
          this.shuffleTriggerCount = 0 // Reset on seed change
          this.shuffledIndices = null
        }

        // For shuffle, we need to map noteIndex through shuffled indices
        // Since shuffle is beat-based like walk, we use the same position calculation
        // but map through the shuffled array
        if (!this.shuffledIndices || this.shuffledIndices.length !== this.data.notes.length) {
          this.shuffledIndices = this.shuffleIndices(this.data.notes.length, seed)
        }
      }

      // Map noteIndex through shuffled indices
      if (this.shuffledIndices && noteIndex < this.shuffledIndices.length) {
        noteIndex = this.shuffledIndices[noteIndex]
      }
    }

    const note = this.data.notes[noteIndex]
    const noteStart = this.noteStarts[noteIndex]
    // For cycleLength, noteStart is from the expanded sequence, but seqPosition is within the cycle
    // We need to calculate the absolute position in the full sequence
    let positionInNote: number
    if (this.data.cycleLength !== undefined && this.data.cycleLength > 0
      && this.data.cycleLength < this.data.notes.length)
    {
      // Calculate absolute position in full sequence
      const cycleIndex = loopLength > 0 ? Math.floor(patternSample / loopLength) : 0
      const numCycles = Math.floor(this.data.notes.length / this.data.cycleLength)
      const cycleOffset = (cycleIndex % numCycles) * loopLength
      const absolutePosition = cycleOffset + seqPosition
      positionInNote = absolutePosition - noteStart
    }
    else {
      positionInNote = seqPosition - noteStart
    }

    // Calculate which beat occurrence we're on (for swing)
    const beatDuration = Math.floor(this.data.notes[0].duration * samplesPerBeat)
    const beatIndex = beatDuration > 0 ? Math.floor(patternSample / beatDuration) : 0
    const isOffBeat = beatIndex % 2 === 1

    // Apply swing offset if we're on an off-beat
    if (isOffBeat && this.data.swing !== undefined && this.data.swing > 0) {
      const swingOffset = Math.floor(beatDuration * this.data.swing * 0.5)
      positionInNote -= swingOffset

      // If swing pushed us back before note start, wrap to previous note
      while (positionInNote < 0 && noteIndex > 0) {
        noteIndex--
        const prevNoteStart = this.noteStarts[noteIndex]
        const prevNoteDuration = Math.floor(this.data.notes[noteIndex].duration * samplesPerBeat)
        positionInNote = prevNoteDuration + positionInNote
      }
    }

    // Calculate position in note (0-1)
    const noteDuration = note.duration * samplesPerBeat
    const positionInNoteNormalized = noteDuration > 0 && positionInNote >= 0
      ? Math.min(1, positionInNote / noteDuration)
      : 0

    // Check if we're at note start (within first sample)
    const isNoteStart = note.velocity >= 0 && positionInNote >= 0 && positionInNote < 1

    const result = {
      noteIndex,
      positionInNote: positionInNoteNormalized,
      isNoteStart,
      currentNote: note,
    }

    return result
  }

  /**
   * Deterministically shuffle array indices using Fisher-Yates algorithm with seeded random
   */
  private shuffleIndices(count: number, seed: number): number[] {
    const indices = Array.from({ length: count }, (_, i) => i)

    // Convert seed to integer with better distribution
    const clampedSeed = Math.max(-1000, Math.min(1000, seed))
    let shuffleSeed = 0
    if (clampedSeed !== 0) {
      const seedInt = Math.floor(Math.abs(clampedSeed) * 1000000)
      shuffleSeed = (seedInt % 2147483647) + 1000003
    }
    else {
      shuffleSeed = 1000003
    }

    // Ensure positive seed and within valid range
    if (shuffleSeed < 0) {
      shuffleSeed = -shuffleSeed
    }
    if (shuffleSeed === 0) {
      shuffleSeed = 1
    }
    if (shuffleSeed >= 2147483647) {
      shuffleSeed = shuffleSeed % 2147483647
    }

    // LCG parameters (well-known good parameters)
    const a = 1664525
    const c = 1013904223
    const m = 2147483647 // 2^31 - 1

    // Fisher-Yates shuffle
    for (let i = count - 1; i > 0; i--) {
      // Generate next random value using LCG
      shuffleSeed = (shuffleSeed * a + c) % m
      // Ensure positive
      if (shuffleSeed < 0) {
        shuffleSeed = shuffleSeed + m
      }
      const randomValue = shuffleSeed / m
      // j is in range [0, i] (inclusive)
      const j = Math.floor(randomValue * (i + 1))

      // Swap indices[i] with indices[j]
      const temp = indices[i]
      indices[i] = indices[j]
      indices[j] = temp
    }

    return indices
  }

  /**
   * Get current note index for trigger-based modes (adv, advslide, rand)
   * Note: These modes require trigger state which we don't track in UI sequencer
   * This is a simplified version that just returns the current position
   */
  getTriggerPosition(
    globalSampleCounter: number,
    bpm: number = 120,
    currentIndex: number = 0,
  ): SequencerPosition | null {
    if (this.data.notes.length === 0) {
      return null
    }

    // For trigger-based modes, we can't accurately predict position without trigger state
    // This is a fallback that assumes we're at the current index
    const noteIndex = currentIndex >= 0 && currentIndex < this.data.notes.length
      ? currentIndex
      : 0

    const note = this.data.notes[noteIndex]

    return {
      noteIndex,
      positionInNote: 0, // Can't determine without trigger state
      isNoteStart: true,
      currentNote: note,
    }
  }

  /**
   * Reset pattern start time (useful when playback starts/stops)
   * With deterministic calculation, this mainly clears BPM tracking
   */
  reset(): void {
    this.patternStartSample = -1
    this.previousBpm = -1
    this.wasmState = null // Clear WASM state so next sync provides fresh state
  }

  /**
   * Reset BPM tracking (useful when program BPM changes)
   */
  resetBpmTracking(): void {
    this.previousBpm = -1
  }

  /**
   * Force recalculation of pattern start from a specific sample position
   * With deterministic calculation, this is now redundant but kept for API compatibility
   */
  syncPatternStart(globalSampleCounter: number, bpm: number = 120): void {
    this.updateTiming(bpm)

    if (this.totalSamples > 0) {
      // Find which pattern iteration we're in based on actual pattern duration
      const patternIteration = Math.floor(globalSampleCounter / this.totalSamples)
      this.patternStartSample = patternIteration * this.totalSamples
    }
    else {
      this.patternStartSample = 0
    }

    this.previousBpm = bpm
  }

  /**
   * Check if pattern start needs initialization
   * Always returns false since pattern start is now calculated deterministically
   */
  needsPatternStartInit(): boolean {
    return false
  }

  /**
   * Get all note start positions in samples (for rendering sequence timeline)
   */
  getNoteStarts(bpm: number = 120): number[] {
    this.updateTiming(bpm)
    return [...this.noteStarts]
  }

  /**
   * Get total sequence length in samples
   */
  getTotalSamples(bpm: number = 120): number {
    this.updateTiming(bpm)
    return this.totalSamples
  }

  /**
   * Get sequence data
   */
  getData(): SequenceData {
    return this.data
  }

  /**
   * Sync state from WASM SequencerState mirror
   * This ensures the UI sequencer matches the exact state of the WASM sequencer
   */
  syncFromWasmState(wasmState: {
    patternStartSample: number
    currentIndex: number
    shuffleTriggerCount: number
    shuffledIndices: Int32Array | null
    rotatedIndices: Int32Array | null
  }): void {
    // Store WASM state and sync timing so UI stays aligned with audio across hot reloads
    this.wasmState = wasmState

    // Sync timing from WASM so pattern start matches the live sequencer
    this.patternStartSample = wasmState.patternStartSample

    // Sync shuffle state
    this.shuffleTriggerCount = wasmState.shuffleTriggerCount
    if (wasmState.shuffledIndices && wasmState.shuffledIndices.length > 0) {
      this.shuffledIndices = Array.from(wasmState.shuffledIndices)
    }
    else {
      this.shuffledIndices = null
    }
  }

  /**
   * Get synced WASM state (if available)
   */
  getWasmState(): typeof this.wasmState {
    return this.wasmState
  }

  /**
   * Check if pattern start should be synced from WASM
   * Always returns false - UI sequencer calculates its own patternStartSample based on current BPM
   */
  shouldSyncPatternStartFromWasm(): boolean {
    return false
  }

  /**
   * Get note start sample positions for a given time range
   * Returns array of { noteIndex, samplePosition } for notes that start in the range
   */
  getNoteStartsInRange(
    startSample: number,
    endSample: number,
    bpm: number = 120,
    mode: SequencerMode = 'walk',
    seed: number = 0,
  ): Array<{ noteIndex: number; samplePosition: number }> {
    if (this.data.notes.length === 0 || this.totalSamples === 0) {
      return []
    }

    const samplesPerBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0

    this.updateTiming(bpm)

    // Align pattern start to bar boundary using the same logic as the AssemblyScript sequencer
    // Use endSample as the reference point (current position)
    // However, if we have synced WASM state, always respect it (WASM is the source of truth)
    // Only recalculate if:
    // 1. We've never synced from WASM (no WASM state), AND
    // 2. patternStartSample is invalid (< 0) OR counter went backwards significantly (reset)
    const samplesPerBar = Math.floor(samplesPerBeat)
    const hasSyncedWasmState = this.wasmState !== null
    const counterReset = this.patternStartSample >= 0 && endSample < this.patternStartSample - 10000

    if (!hasSyncedWasmState && (this.patternStartSample < 0 || counterReset)) {
      const safeSamplesPerBar = samplesPerBar > 0 ? samplesPerBar : 1
      this.patternStartSample = endSample - (endSample % safeSamplesPerBar)
    }

    this.previousBpm = bpm

    const offsetSamples = this.data.offset !== 0 ? Math.floor(this.data.offset! * this.sampleRate) : 0
    const events: Array<{ noteIndex: number; samplePosition: number }> = []

    // Handle shuffle mode
    let shuffledIndices: number[] | null = null
    if (mode === 'shuffle') {
      if (seed !== this.lastShuffleSeed) {
        this.lastShuffleSeed = seed
        this.shuffledIndices = null
      }
      if (!this.shuffledIndices || this.shuffledIndices.length !== this.data.notes.length) {
        this.shuffledIndices = this.shuffleIndices(this.data.notes.length, seed)
      }
      shuffledIndices = this.shuffledIndices
    }

    // Convert global samples to pattern-relative samples
    const startTimeSinceStart = startSample - this.patternStartSample
    const endTimeSinceStart = endSample - this.patternStartSample
    const startPatternSample = startTimeSinceStart + offsetSamples
    const endPatternSample = endTimeSinceStart + offsetSamples

    // Add small epsilon to boundaries to account for rounding errors
    // This ensures we don't miss events due to floating point precision issues
    // Use a larger epsilon for end to account for Math.floor rounding in noteStarts calculation
    // and conversion between pattern space and global space
    const startEpsilon = 1.0 // Small tolerance for start boundary (rounding errors)
    const endEpsilon = 10.0 // Larger tolerance for end boundary (rounding + conversions)
    const startPatternSampleWithEpsilon = startPatternSample - startEpsilon
    const endPatternSampleWithEpsilon = endPatternSample + endEpsilon

    // Find which pattern loops overlap the range
    // Use cycleLength if set (for nested alternating patterns), otherwise use totalSamples
    const loopLength = this.data.cycleLength !== undefined
      ? this.calculateCycleLengthSamples(this.data.cycleLength, bpm)
      : this.totalSamples
    const startPatternLoop = Math.floor((startPatternSample - startEpsilon) / loopLength)
    const endPatternLoop = Math.ceil((endPatternSample + endEpsilon) / loopLength)

    // Ensure we check at least one loop even if the range is very small
    if (endPatternLoop < startPatternLoop) {
      return []
    }

    // Iterate through ALL pattern loops that could contain events in the range
    // For each loop, check notes to see if they fall within the range
    for (let loop = startPatternLoop; loop <= endPatternLoop; loop++) {
      const loopStart = loop * loopLength

      // Determine which notes to check for this loop
      let noteStartIndex = 0
      let noteEndIndex = this.data.notes.length
      if (this.data.cycleLength !== undefined && this.data.cycleLength > 0
        && this.data.cycleLength < this.data.notes.length)
      {
        // For nested alternating patterns, only check notes in the current cycle
        const numCycles = Math.floor(this.data.notes.length / this.data.cycleLength)
        let actualCycleIndex = loop % numCycles
        // Handle negative modulo results
        if (actualCycleIndex < 0) {
          actualCycleIndex += numCycles
        }
        noteStartIndex = actualCycleIndex * this.data.cycleLength
        noteEndIndex = noteStartIndex + this.data.cycleLength
      }

      // Check notes in the relevant range
      for (let n = noteStartIndex; n < noteEndIndex && n < this.data.notes.length; n++) {
        // For cycleLength, noteStartInPattern needs to be relative to the cycle start
        let noteStartInPattern = this.noteStarts[n]
        if (this.data.cycleLength !== undefined && this.data.cycleLength > 0
          && this.data.cycleLength < this.data.notes.length)
        {
          // Calculate note start relative to cycle start
          const cycleStartIndex = noteStartIndex
          const noteIndexInCycle = n - cycleStartIndex
          // Calculate relative start by summing durations of previous notes in cycle
          let relativeStart = 0
          for (let i = 0; i < noteIndexInCycle; i++) {
            const noteDuration = this.data.notes[cycleStartIndex + i].duration * samplesPerBeat
            relativeStart += Math.floor(noteDuration)
          }
          noteStartInPattern = relativeStart
        }
        const absolutePatternSample = loopStart + noteStartInPattern

        // Calculate the global sample position for this event
        // First, apply swing if needed (this adjusts the pattern sample)
        const beatDuration = Math.floor(this.data.notes[0].duration * samplesPerBeat)
        const beatIndex = beatDuration > 0 ? Math.floor(absolutePatternSample / beatDuration) : 0
        const isOffBeat = beatIndex % 2 === 1

        let adjustedPatternSample = absolutePatternSample
        if (isOffBeat && this.data.swing !== undefined && this.data.swing > 0) {
          const swingOffset = Math.floor(beatDuration * this.data.swing * 0.5)
          adjustedPatternSample -= swingOffset
        }

        // Convert pattern sample to global sample
        // patternSample = (globalSample - patternStartSample) + offsetSamples
        // So: globalSample = patternSample - offsetSamples + patternStartSample
        const globalSample = adjustedPatternSample - offsetSamples + this.patternStartSample

        // Check if this event's global sample position falls within the requested range
        // Use inclusive boundaries: [startSample, endSample]
        // Add epsilon tolerance to account for rounding errors in calculations
        const rangeEpsilon = 50.0 // Large tolerance to catch all rounding errors
        if (globalSample >= startSample - rangeEpsilon && globalSample <= endSample + rangeEpsilon) {
          // Determine the note index (handle shuffle mode)
          let noteIndex = n
          if (shuffledIndices && noteIndex < shuffledIndices.length) {
            noteIndex = shuffledIndices[noteIndex]
          }

          // Only include if velocity >= 0 (not a rest)
          if (this.data.notes[noteIndex].velocity >= 0) {
            events.push({ noteIndex, samplePosition: globalSample })
          }
        }
      }
    }

    // Sort by sample position
    events.sort((a, b) => a.samplePosition - b.samplePosition)

    return events
  }
}

export interface SequenceConfig {
  id: number
  mode: SequencerMode
  seed?: number // for shuffle mode
}

export interface NoteEvent {
  sequenceId: number
  noteIndex: number
  samplePosition: number
  note: NoteData
}

/**
 * Manager for batch updates of multiple sequences
 * Tracks state between updates and returns all note events that occurred
 */
export class SequencerManager {
  private sequencers = new Map<number, UISequencer>()
  private configs = new Map<number, SequenceConfig>()
  private lastSampleCounter: number = -1
  private lastNoteIndices = new Map<number, number>()
  private lastPositionInNote = new Map<number, number>()
  private oversamplingFactor: number = 1
  private sampleRate: number = SAMPLE_RATE
  private enabledSequenceIds: Set<number> | null = null

  constructor(oversamplingFactor: number = 1, sampleRate: number = SAMPLE_RATE) {
    this.oversamplingFactor = oversamplingFactor
    this.sampleRate = sampleRate
  }

  /**
   * Set sequences to track
   * @param sequences Map of sequenceId -> SequenceData
   * @param configs Map of sequenceId -> SequenceConfig (mode, seed, etc.)
   */
  setSequences(
    sequences: Map<number, SequenceData> | SequenceData[],
    configs?: Map<number, SequenceConfig> | SequenceConfig[],
  ): void {
    // Clear existing sequencers
    this.sequencers.clear()
    this.configs.clear()
    this.lastNoteIndices.clear()
    this.lastPositionInNote.clear()

    // Handle array or map input
    const seqMap = Array.isArray(sequences)
      ? new Map(sequences.map(s => [s.id, s]))
      : sequences

    const configMap = configs
      ? (Array.isArray(configs) ? new Map(configs.map(c => [c.id, c])) : configs)
      : new Map<number, SequenceConfig>()

    // Create sequencers for each sequence
    for (const [id, data] of seqMap) {
      const sequencer = new UISequencer(data, this.oversamplingFactor, this.sampleRate)
      this.sequencers.set(id, sequencer)

      // Detect trigger-based sequences (rand, adv, advslide)
      // Rand sequences: all notes have duration exactly 1, and offset property is undefined (not present)
      // Walk sequences always have offset property (even if 0), so they won't match
      // Only detect as rand if ALL notes have duration exactly 1 AND offset property is undefined
      const allNotesDuration1 = data.notes.length > 0 && data.notes.every(note => Math.abs(note.duration - 1) < 0.0001)
      const hasOffsetProperty = data.offset !== undefined // Walk sequences always have offset property (even if 0)
      const isTriggerBased = allNotesDuration1 && !hasOffsetProperty
      const defaultMode: SequencerMode = isTriggerBased ? 'rand' : 'walk'

      this.configs.set(id, configMap.get(id) || { id, mode: defaultMode })
    }

    // Note: resetSequencers() should be called explicitly with current sample counter
    // to ensure proper sync when switching tabs
  }

  /**
   * Set which sequence IDs should emit events
   * @param sequenceIds Set of sequence IDs to enable, or null to disable all sequences
   */
  setEnabledSequences(sequenceIds: Set<number> | null): void {
    // If enabling sequences after being disabled, or if the set of sequences changed,
    // clear tracking state to ensure first update emits events
    if (this.enabledSequenceIds === null && sequenceIds !== null) {
      // Re-enabling after being disabled - clear all tracking
      this.lastNoteIndices.clear()
      this.lastPositionInNote.clear()
    }
    else if (sequenceIds !== null && this.enabledSequenceIds !== null) {
      // Check if the set of enabled sequences changed
      const idsChanged = sequenceIds.size !== this.enabledSequenceIds.size
        || ![...sequenceIds].every(id => this.enabledSequenceIds!.has(id))

      if (idsChanged) {
        // Clear tracking for sequences that weren't previously enabled
        for (const id of sequenceIds) {
          if (!this.enabledSequenceIds.has(id)) {
            this.lastNoteIndices.delete(id)
            this.lastPositionInNote.delete(id)
          }
        }
      }
    }

    this.enabledSequenceIds = sequenceIds
  }

  /**
   * Update and return note events for sequences whose current note changed
   * @param globalSampleCounter Current global sample counter
   * @param bpm Current BPM
   * @returns Array of note events for notes that are currently active
   */
  update(globalSampleCounter: number, bpm: number = 120): NoteEvent[] {
    const events: NoteEvent[] = []

    // If sample counter went backwards (reset), clear tracking state
    // With deterministic position calculation, we don't need to reset sequencers
    // Just clear the note change tracking to force re-emission of current state
    if (this.lastSampleCounter >= 0 && globalSampleCounter < this.lastSampleCounter) {
      this.lastNoteIndices.clear()
      this.lastPositionInNote.clear()
      this.lastSampleCounter = -1 // Reset to force first event emission
    }

    // If this is the first update after initialization (lastSampleCounter is -1),
    // and counter is very low, force emission of note 0 to ensure we start from the beginning
    const isFirstUpdate = this.lastSampleCounter < 0
    const isCounterLow = globalSampleCounter < 1000

    // Check current position for each sequence
    for (const [sequenceId, sequencer] of this.sequencers) {
      // Skip sequences that are not enabled
      if (this.enabledSequenceIds === null || !this.enabledSequenceIds.has(sequenceId)) {
        continue
      }

      const config = this.configs.get(sequenceId) || { id: sequenceId, mode: 'walk' }

      // For rand mode, use trigger-based position from WASM state
      let position: SequencerPosition | null
      if (config.mode === 'rand') {
        const wasmState = sequencer.getWasmState()
        const currentIndex = wasmState?.currentIndex ?? 0
        position = sequencer.getTriggerPosition(globalSampleCounter, bpm, currentIndex)
      }
      else {
        position = sequencer.getPosition(globalSampleCounter, bpm, config.mode, config.seed || 0)
      }

      if (!position) {
        continue
      }

      const lastNoteIndex = this.lastNoteIndices.get(sequenceId)
      const lastPosition = this.lastPositionInNote.get(sequenceId)
      let currentNoteIndex = position.noteIndex
      const currentPosition = position.positionInNote

      // If this is the first update and counter is low, force note 0 to ensure we start from the beginning
      // Only for time-based modes (not rand/trigger-based)
      if (isFirstUpdate && isCounterLow && currentNoteIndex !== 0 && config.mode !== 'rand') {
        // Get position at counter 0 to find note 0
        const positionAtZero = sequencer.getPosition(0, bpm, config.mode, config.seed || 0)
        if (positionAtZero) {
          currentNoteIndex = positionAtZero.noteIndex
        }
      }

      // Detect note trigger by checking if:
      // 1. First time seeing this sequence (always emit to show current state)
      // 2. Note index changed (moved to different note)
      // 3. Position decreased (note looped/retriggered)
      const positionDecreased = lastPosition !== undefined
        && currentPosition < lastPosition

      const noteTriggered = lastNoteIndex === undefined
        || lastNoteIndex !== currentNoteIndex
        || positionDecreased

      if (noteTriggered) {
        const data = sequencer.getData()
        const note = data.notes[currentNoteIndex]
        const event = {
          sequenceId,
          noteIndex: currentNoteIndex,
          samplePosition: globalSampleCounter,
          note,
        }
        events.push(event)
      }

      // Update tracked state
      this.lastNoteIndices.set(sequenceId, currentNoteIndex)
      this.lastPositionInNote.set(sequenceId, currentPosition)
    }

    // Update last sample counter
    this.lastSampleCounter = globalSampleCounter

    return events
  }

  /**
   * Reset all sequencers (useful when playback starts/stops)
   */
  reset(): void {
    for (const sequencer of this.sequencers.values()) {
      sequencer.reset()
    }
    this.lastNoteIndices.clear()
    this.lastPositionInNote.clear()
    this.lastSampleCounter = -1
  }

  /**
   * Reset all sequencers (useful when playback starts/stops)
   * Note: Don't manually reset pattern start when switching tabs - let it calculate naturally
   * from globalSampleCounter, matching AssemblyScript sequencer behavior
   */
  resetSequencers(): void {
    for (const sequencer of this.sequencers.values()) {
      sequencer.reset()
    }
    this.lastNoteIndices.clear()
    this.lastPositionInNote.clear()
  }

  /**
   * Initialize the last sample counter to current position
   * This prevents missing events on the first update after initialization
   */
  initializeCounter(globalSampleCounter: number, bpm: number = 120): void {
    this.lastSampleCounter = globalSampleCounter
    this.lastNoteIndices.clear()
    this.lastPositionInNote.clear()
  }

  /**
   * Initialize pattern starts for all sequencers from current position
   * This ensures they're synced with AssemblyScript sequencers
   */
  initializePatternStarts(globalSampleCounter: number, bpm: number = 120): void {
    for (const sequencer of this.sequencers.values()) {
      if (sequencer.needsPatternStartInit()) {
        sequencer.syncPatternStart(globalSampleCounter, bpm)
      }
    }
  }

  /**
   * Get current position for a specific sequence (for continuous display)
   */
  getPosition(sequenceId: number, globalSampleCounter: number, bpm: number = 120): SequencerPosition | null {
    const sequencer = this.sequencers.get(sequenceId)
    if (!sequencer) {
      return null
    }

    const config = this.configs.get(sequenceId) || { id: sequenceId, mode: 'walk' }
    return sequencer.getPosition(globalSampleCounter, bpm, config.mode, config.seed || 0)
  }

  /**
   * Get current positions for all sequences (for continuous display)
   */
  getAllPositions(globalSampleCounter: number, bpm: number = 120): Map<number, SequencerPosition> {
    const positions = new Map<number, SequencerPosition>()
    for (const [sequenceId, sequencer] of this.sequencers) {
      const config = this.configs.get(sequenceId) || { id: sequenceId, mode: 'walk' }
      const position = sequencer.getPosition(globalSampleCounter, bpm, config.mode, config.seed || 0)
      if (position) {
        positions.set(sequenceId, position)
      }
    }
    return positions
  }

  /**
   * Sync all sequencers from WASM state mirrors
   * @param wasmStates Map of sequenceId -> SequencerStateMirror
   */
  syncFromWasmStates(
    wasmStates: Map<number, {
      patternStartSample: number
      currentIndex: number
      shuffleTriggerCount: number
      shuffledIndices: Int32Array | null
      rotatedIndices: Int32Array | null
    }>,
  ): void {
    let syncedCount = 0
    for (const [sequenceId, sequencer] of this.sequencers) {
      const wasmState = wasmStates.get(sequenceId)
      if (wasmState) {
        sequencer.syncFromWasmState(wasmState)
        syncedCount++
      }
    }
  }

  /**
   * Reset BPM tracking for all sequencers (useful when program BPM changes)
   */
  resetBpmTracking(): void {
    for (const sequencer of this.sequencers.values()) {
      sequencer.resetBpmTracking()
    }
  }
}
